// Inventory, fish freshness, village market pricing/trading, upgrade costs,
// and the rival/season scoring simulation.
// Extracted from legacy/tsuri.html — logic and constants are unchanged.
//
// This module is intentionally free of DOM and Three.js calls (per the
// project's multiplayer note: economy state should be server-shareable
// later). Anything that needs to touch a mesh, the DOM, or a system that
// isn't extracted yet (boat/cart/jetty in building.ts, weather, rest/sleep,
// the fishing net) takes that value in as a parameter or reports back via
// a return value instead of reaching for a global.
//
// Left out of this module entirely (no home yet in the 10-step plan):
// storm damage/repair, rest/sleep/shelter, day/night/weather, and the
// fishing-net mechanic. These will need explicit wiring in main.ts.

import {
  SEA_LEVEL, PEAK_LEVEL, hexToWorld, worldToHex, axialDistance, terrainHeightAt, hash2, getTerrainSeed,
} from '@world/terrain';
import { FISH_TYPES } from '@entities/fish';
import { increaseCastRange } from '@systems/fishing';
import { getActiveAvatar } from '@entities/avatars';
import type { PlayerStats } from '@net/protocol';

// ================= Inventory & storage =================
export let wood = 0; // he starts with nothing but his rod and axe
export let coins = 0;
export const CARRY_CAP = 5;
export let BOAT_CAP = 20, CART_CAP = 20; // grow with upgrades

export interface StoredFish {
  n: string;
  f: number;
  salted?: boolean;
}

export const carryFish: StoredFish[] = [];
export const boatFish: StoredFish[] = [];
export const cartFish: StoredFish[] = [];
export let pearls = 0;
export function addPearls(n: number): void {
  pearls += n;
}
export const PEARL_PRICE = 18;

// ---- Freshness & ice ----
// A fish starts with 3 days of freshness and ages each dawn (half as
// fast with himuro ice in stock). Aging fish sell for less; spoiled
// fish are tossed at dawn.
export let iceDaysLeft = 0;
export let saltLeft = 0; // each unit salts one fish
export const SALT_VALUE = 0.6; // salted fish sell flat at 60% of base price

export function makeFish(name: string): StoredFish {
  return { n: name, f: 3 };
}

export function freshMult(fish: StoredFish): number {
  if (fish.salted) return SALT_VALUE;
  if (fish.f >= 2.5) return 1;
  if (fish.f >= 1.5) return 0.75;
  return 0.4;
}

export let rodLevel = 1, boatLevel = 1, cartLevel = 1;
export let hasNet = false;

// Lifetime stats for the season's awards
export let statCaught = 0;
// Lifetime catch count per species — the Naturalist award only needs
// .size (distinct species), but the count per species is what lets the
// standings breakdown show "what types" rather than just a number.
export const speciesCounts = new Map<string, number>();
export const fishedAreas = new Set<string>(); // coarse buckets of distinct waters fished
export let jettyCount = 0;

export function recordCatch(name: string, x: number, z: number): void {
  statCaught++;
  speciesCounts.set(name, (speciesCounts.get(name) ?? 0) + 1);
  fishedAreas.add(areaBucket(x, z));
}

export function incrementJettyCount(): void {
  jettyCount++;
}

export function areaBucket(x: number, z: number): string {
  const h = worldToHex(x, z);
  return Math.round(h.q / 6) + ',' + Math.round(h.r / 6);
}

export function woodCount(): number {
  return wood;
}
export function addWood(n: number): void {
  wood += n;
}
export function spendWood(n: number): void {
  wood -= n;
}
export function addCoins(n: number): void {
  coins += n;
}

export function transferFish(from: StoredFish[], to: StoredFish[], cap: number): number {
  let n = 0;
  while (from.length && to.length < cap) {
    to.push(from.shift()!);
    n++;
  }
  return n;
}

// Ages every stored fish overnight; himuro ice halves the aging.
// Returns the number of fish that spoiled and had to be discarded.
export function ageFishOvernight(): number {
  const ageStep = iceDaysLeft > 0 ? 0.5 : 1;
  let spoiled = 0;
  [carryFish, boatFish, cartFish].forEach((arr) => {
    for (let i = arr.length - 1; i >= 0; i--) {
      if (arr[i].salted) continue; // salted fish keep forever
      arr[i].f -= ageStep;
      if (arr[i].f <= 0) { arr.splice(i, 1); spoiled++; }
    }
  });
  if (iceDaysLeft > 0) iceDaysLeft--;
  return spoiled;
}

// ================= Villages & the market economy =================
// One village per region of the world, sited deterministically on flat
// dry land. Each has its own local demand per species: selling floods
// the local supply and drops that price; demand recovers over time.
export const VILLAGE_REGION = 44;

export interface Village {
  ok: boolean;
  q: number;
  r: number;
  x: number;
  z: number;
  demand: Record<string, number>;
  mult: Record<string, number>;
  lastT: number;
}

export const villages = new Map<string, Village>();

// Hand-placed villages that don't fit the one-per-region model below (used
// by world/fixedMap.ts — a single VILLAGE_REGION cell is far bigger than
// the whole fixed board, so two fixed markets can't both be cached under
// real region keys). Empty for every normal procedural game.
export const extraVillages: Village[] = [];

export const BASE_PRICE: Record<string, number> = {
  Perch: 3, Trout: 5, Bass: 6, Carp: 4, Salmon: 8, Tilapia: 4, Pike: 9, Catfish: 7,
};
export const WOOD_BUNDLE = 5, WOOD_SELL_PRICE = 6, WOOD_BUY_PRICE = 10;
export const CART_PRICE = 35; // coins

export function villageFor(rx: number, rz: number, now: number): Village {
  const rk = rx + ',' + rz;
  let v = villages.get(rk);
  if (v) return v;
  const aq = Math.round(rx * VILLAGE_REGION + (hash2(rx * 71 + 13, rz * 53 + 7) - 0.5) * VILLAGE_REGION * 0.5);
  const ar = Math.round(rz * VILLAGE_REGION + (hash2(rx * 37 + 3, rz * 97 + 29) - 0.5) * VILLAGE_REGION * 0.5);
  let site: { q: number; r: number } | null = null;
  outer:
  // Requiring an exactly-flat 2-ring footprint (below) is a much stricter
  // ask than the old ±1-level, ring-1-only check, so the search needs a
  // wider net to still find a site almost everywhere — this is cheap
  // (each siting is computed once and cached in `villages`).
  for (let radius = 0; radius < 45; radius++) {
    for (let dq = -radius; dq <= radius; dq++) {
      const rMin = Math.max(-radius, -dq - radius), rMax = Math.min(radius, -dq + radius);
      for (let dr = rMin; dr <= rMax; dr++) {
        if (axialDistance(0, 0, dq, dr) !== radius) continue;
        const q = aq + dq, r = ar + dr;
        const h = terrainHeightAt(q, r);
        if (h < SEA_LEVEL + 1 || h >= PEAK_LEVEL - 1) continue;
        // The village mesh (chunks.ts's buildVillageMesh) is a single rigid
        // group at one fixed height, and its outlying pieces (the hut sits
        // ~1.5 hex-widths from center) reach past the immediate ring of
        // neighbors. A site is only "flat" if every hex under that whole
        // footprint is the exact same height — even a 1-level step buries
        // part of the building in the taller neighbor's terrain block.
        let flat = true;
        outerFlat:
        for (let fr = 1; fr <= 2; fr++) {
          for (let fq = -fr; fq <= fr; fq++) {
            const frMin = Math.max(-fr, -fq - fr), frMax = Math.min(fr, -fq + fr);
            for (let frr = frMin; frr <= frMax; frr++) {
              if (axialDistance(0, 0, fq, frr) !== fr) continue;
              const nh = terrainHeightAt(q + fq, r + frr);
              if (nh !== h) { flat = false; break outerFlat; }
            }
          }
        }
        if (flat) { site = { q, r }; break outer; }
      }
    }
  }
  const mult: Record<string, number> = {}, demand: Record<string, number> = {};
  FISH_TYPES.forEach((t, i) => {
    mult[t.name] = 0.85 + hash2(aq * 13 + i * 7, ar * 17 + i * 3) * 0.4;
    demand[t.name] = 1;
  });
  const vq = site ? site.q : aq, vr = site ? site.r : ar;
  const pos = hexToWorld(vq, vr);
  v = { ok: !!site, q: vq, r: vr, x: pos.x, z: pos.z, demand, mult, lastT: now };
  villages.set(rk, v);
  return v;
}

export function nearestVillage(x: number, z: number, now: number): { v: Village; d: number } | null {
  const pq = worldToHex(x, z);
  const rx0 = Math.round(pq.q / VILLAGE_REGION), rz0 = Math.round(pq.r / VILLAGE_REGION);
  let best: Village | null = null, bestD = Infinity;
  for (let rx = rx0 - 1; rx <= rx0 + 1; rx++) {
    for (let rz = rz0 - 1; rz <= rz0 + 1; rz++) {
      const v = villageFor(rx, rz, now);
      if (!v.ok) continue;
      const d = Math.hypot(v.x - x, v.z - z);
      if (d < bestD) { bestD = d; best = v; }
    }
  }
  extraVillages.forEach((v) => {
    const d = Math.hypot(v.x - x, v.z - z);
    if (d < bestD) { bestD = d; best = v; }
  });
  return best ? { v: best, d: bestD } : null;
}

// Scans the 3x3 block of regions around (q, r), the same way nearestVillage
// does, rather than only the region the hex itself falls in. A village is
// anchored with up to half a region of jitter and can then search outward
// for a flat site, so it routinely ends up sited inside a *neighbouring*
// region — checking one region only would miss it, and trees would grow on
// top of the market.
export function isNearVillageSite(q: number, r: number, range: number, now: number): boolean {
  const rx0 = Math.round(q / VILLAGE_REGION), rz0 = Math.round(r / VILLAGE_REGION);
  for (let rx = rx0 - 1; rx <= rx0 + 1; rx++) {
    for (let rz = rz0 - 1; rz <= rz0 + 1; rz++) {
      const v = villageFor(rx, rz, now);
      if (v.ok && axialDistance(q, r, v.q, v.r) <= range) return true;
    }
  }
  return extraVillages.some((ev) => axialDistance(q, r, ev.q, ev.r) <= range);
}

// ---- Trading ----
export function priceFor(v: Village, name: string, now: number): number {
  const el = (now - v.lastT) / 1000;
  if (el > 1) {
    FISH_TYPES.forEach((t) => { v.demand[t.name] = Math.min(1.25, v.demand[t.name] + el * 0.003); });
    v.lastT = now;
  }
  return Math.max(1, Math.round(BASE_PRICE[name] * v.mult[name] * v.demand[name]));
}

export function sellSpecies(v: Village, name: string, cartNearby: boolean, now: number): { sold: number; earned: number } {
  let earned = 0, sold = 0;
  function sellFrom(arr: StoredFish[]): void {
    for (let i = arr.length - 1; i >= 0; i--) {
      if (arr[i].n !== name) continue;
      if (arr[i].salted) {
        earned += Math.max(1, Math.round(BASE_PRICE[name] * SALT_VALUE)); // preserved goods: flat rate
      } else {
        earned += Math.max(1, Math.round(priceFor(v, name, now) * freshMult(arr[i])));
        v.demand[name] = Math.max(0.35, v.demand[name] * 0.955); // only fresh supply moves the market
      }
      arr.splice(i, 1);
      sold++;
    }
  }
  sellFrom(carryFish);
  if (cartNearby) sellFrom(cartFish);
  if (sold) addCoins(earned);
  return { sold, earned };
}

export function sellWood(): { success: boolean } {
  if (wood < WOOD_BUNDLE) return { success: false };
  spendWood(WOOD_BUNDLE);
  addCoins(WOOD_SELL_PRICE);
  return { success: true };
}

export function buyWood(): { success: boolean } {
  if (coins < WOOD_BUY_PRICE) return { success: false };
  coins -= WOOD_BUY_PRICE;
  addWood(WOOD_BUNDLE);
  return { success: true };
}

export function sellPearls(): { sold: number; earned: number } {
  if (!pearls) return { sold: 0, earned: 0 };
  const earned = pearls * PEARL_PRICE;
  const sold = pearls;
  pearls = 0;
  addCoins(earned);
  return { sold, earned };
}

// Deducts the cart's price if affordable. The caller is responsible for
// checking whether a cart is already owned and for actually spawning it
// (building.ts) once purchase succeeds.
export function buyCart(): { success: boolean } {
  if (coins < CART_PRICE) return { success: false };
  coins -= CART_PRICE;
  return { success: true };
}

// Generic coin spend for the lantern and its oil — the lantern's own state
// lives in systems/lantern.ts, so the market only handles the money.
export function spendCoins(amount: number): boolean {
  if (coins < amount) return false;
  coins -= amount;
  return true;
}

// ================= Upgrades =================
export function upgradeRod(price: number): { success: boolean; newLevel: number } {
  if (coins < price) return { success: false, newLevel: rodLevel };
  coins -= price;
  rodLevel++;
  increaseCastRange(1.2);
  return { success: true, newLevel: rodLevel };
}

// Reports the speed bonus for the caller to apply to the boat entity
// (building.ts owns boat speed/mesh); economy only owns the cap and cost.
export function upgradeBoat(): { success: boolean; capBonus: number; speedBonus: number } {
  if (coins < 60) return { success: false, capBonus: 0, speedBonus: 0 };
  coins -= 60;
  boatLevel = 2;
  BOAT_CAP += 12;
  return { success: true, capBonus: 12, speedBonus: 0.8 };
}

export function upgradeCart(): { success: boolean; capBonus: number } {
  if (coins < 50) return { success: false, capBonus: 0 };
  coins -= 50;
  cartLevel = 2;
  CART_CAP += 15;
  return { success: true, capBonus: 15 };
}

export function buyNet(): { success: boolean } {
  if (coins < 25) return { success: false };
  coins -= 25;
  hasNet = true;
  return { success: true };
}

export function buyIce(): { success: boolean; reason?: string } {
  if (coins < 10) return { success: false, reason: 'not-enough-coins' };
  if (iceDaysLeft >= 6) return { success: false, reason: 'chest-full' };
  coins -= 10;
  iceDaysLeft = Math.min(6, iceDaysLeft + 3);
  return { success: true };
}

export function buySalt(): { success: boolean; reason?: string } {
  if (coins < 6) return { success: false, reason: 'not-enough-coins' };
  if (saltLeft >= 15) return { success: false, reason: 'salt-full' };
  coins -= 6;
  saltLeft = Math.min(15, saltLeft + 5);
  return { success: true };
}

export function saltCatch(): { salted: number; reason?: string } {
  if (saltLeft <= 0) return { salted: 0, reason: 'no-salt' };
  // Salt the fish closest to spoiling first
  const candidates: StoredFish[] = [];
  [carryFish, boatFish, cartFish].forEach((arr) => {
    arr.forEach((fish) => { if (!fish.salted) candidates.push(fish); });
  });
  if (!candidates.length) return { salted: 0, reason: 'nothing-to-salt' };
  candidates.sort((a, b) => a.f - b.f);
  let n = 0;
  while (saltLeft > 0 && n < candidates.length) {
    candidates[n].salted = true;
    saltLeft--;
    n++;
  }
  return { salted: n };
}

// ================= Rivals & the Season =================
export const SEASON_DAYS = 21;
export let seasonOver = false;

export function checkSeasonEnd(dayIndex: number): boolean {
  if (dayIndex >= SEASON_DAYS) seasonOver = true;
  return seasonOver;
}

export let rivalCount = 2;
export function setRivalCount(n: number): void {
  rivalCount = n;
}
export const RIVAL_NAMES = ['Old Genji', 'Hana', 'Taro', 'Ume'];

export interface Rival {
  name: string;
  coins: number;
  gear: number;
  cargo: string[];
  cap: number;
  skill: number;
  note: string;
  totalCaught: number;
  species: Set<string>;
  areas: number;
  wood: number;
}

export const rivals: Rival[] = [];

let rivalSeed = 0;

// Call once, right after world/terrain.ts's applySeed(n) (same pattern as
// world/weather.ts's applyWeatherSeed) — rival skill and each day's catch
// simulation are drawn from this instead of Math.random(), so every
// client's local rival simulation produces identical trophy-relevant
// stats (catch count, species, areas roamed, wood gained) for the same
// day rather than silently diverging per device. Selling price and gear-
// purchase timing still depend on each client's own locally-discovered
// villages (never networked), so rival coins/gear can still drift a
// little between clients — only the fishing-activity stats are pinned.
export function applyRivalSeed(): void {
  const { q } = getTerrainSeed();
  rivalSeed = Math.floor(q * 613) % 100000;
}

function rivalRoll(rivalIndex: number, day: number, draw: number): number {
  return hash2(day * 977 + rivalIndex * 131 + draw, rivalSeed);
}

// excludeNames defaults to just the local avatar (solo play — there's no
// one else to conflict with). Multiplayer's game_start handler instead
// passes every room player's avatar name (synced via the shared player
// list), so all clients build the exact same rival roster/order — without
// that, two players excluding different names from RIVAL_NAMES would
// attach the same seeded stats to different-named rivals on each screen.
export function setupRivals(excludeNames: string[] = [getActiveAvatar().name]): void {
  rivals.length = 0;
  const namePool = RIVAL_NAMES.filter((n) => !excludeNames.includes(n));
  for (let i = 0; i < rivalCount && i < namePool.length; i++) {
    rivals.push({
      name: namePool[i],
      coins: 0,
      gear: 0,
      cargo: [],
      cap: 10,
      skill: 0.8 + rivalRoll(i, -1, 0) * 0.25,
      note: 'setting out',
      totalCaught: 0,
      species: new Set(),
      areas: 1,
      wood: 0,
    });
  }
}

const RIVAL_GEAR = [
  { cost: 35, note: 'bought a better rod' },
  { cost: 60, note: 'launched a boat', cap: 10 },
  { cost: 50, note: 'bought a cart', cap: 10 },
  { cost: 70, note: 'mastered the rod' },
];
const SHALLOW_SPECIES = ['Perch', 'Trout', 'Bass', 'Carp', 'Salmon', 'Tilapia'];
const DEEP_SPECIES = ['Catfish', 'Pike', 'Carp', 'Salmon'];

export function knownVillages(): Village[] {
  const list: Village[] = [];
  villages.forEach((v) => { if (v.ok) list.push(v); });
  return list;
}

// One rival's day: fish according to gear/weather/skill, sell a full
// cargo into a real village market (moving its prices!), reinvest.
// `day`/`rivalIndex` seed the fishing-activity rolls (see rivalRoll) so
// every client simulates the same catch for the same rival on the same
// day; village choice/pricing below still uses Math.random() since it
// depends on each client's own locally-discovered villages anyway.
export function simulateRivalDay(rv: Rival, weather: string, now: number, day: number, rivalIndex: number): void {
  let draw = 0;
  const roll = () => rivalRoll(rivalIndex, day, draw++);

  if (weather === 'Storm') {
    if (rv.gear >= 2 && roll() < 0.5) {
      rv.coins = Math.max(0, rv.coins - 5);
      rv.note = 'repaired storm damage';
    } else {
      rv.note = 'waited out the storm';
    }
    return;
  }
  const rainBonus = weather === 'Rain' ? 1.25 : 1;
  let n = Math.round((2.2 + rv.gear * 1.4) * rv.skill * rainBonus * (0.7 + roll() * 0.6));
  n = Math.max(0, Math.min(n, rv.cap - rv.cargo.length));
  const pool = rv.gear >= 2 ? DEEP_SPECIES : SHALLOW_SPECIES;
  for (let i = 0; i < n; i++) {
    const nm = pool[Math.floor(roll() * pool.length)];
    rv.cargo.push(nm);
    rv.species.add(nm);
  }
  rv.totalCaught += n;
  rv.wood += Math.floor(roll() * 3); // chops on the side
  if (roll() < 0.35) rv.areas++;     // roams to new waters
  rv.note = n > 0 ? 'caught ' + n + ' fish' : 'had no luck';

  if (rv.cargo.length >= rv.cap * 0.75) {
    const vs = knownVillages();
    let earned = 0;
    if (vs.length) {
      const v = vs[Math.floor(Math.random() * vs.length)];
      rv.cargo.forEach((name) => {
        earned += Math.max(1, Math.round(priceFor(v, name, now) * 0.85)); // their catch has aged a little too
        v.demand[name] = Math.max(0.35, v.demand[name] * 0.955);
      });
    } else {
      rv.cargo.forEach((name) => { earned += Math.round(BASE_PRICE[name] * 0.85); });
    }
    rv.coins += earned;
    rv.note = 'sold ' + rv.cargo.length + ' fish for ' + earned + '🪙';
    rv.cargo.length = 0;
    const up = RIVAL_GEAR[rv.gear];
    if (up && rv.coins >= up.cost + 15) {
      rv.coins -= up.cost;
      rv.gear++;
      if (up.cap) rv.cap += up.cap;
      rv.wood = Math.max(0, rv.wood - 8);
      rv.note += ', ' + up.note;
    }
  }
}

export function rivalCargoValue(rv: Rival): number {
  return rv.cargo.reduce((s, name) => s + BASE_PRICE[name], 0);
}

// ---- Points & awards ----
// Points = coins + sellable goods, plus season awards: each category's
// leader(s) claim the bonus. Different playstyles, different trophies.
export interface Contestant {
  name: string;
  you?: boolean;
  base: number;
  caught: number;
  species: number;
  areas: number;
  infra: number;
  upgrades: number;
  wood: number;
  pts: number;
  trophies: string;
}

export interface AwardDef {
  icon: string;
  name: string;
  desc: string;
  pts: number;
  get: (c: Contestant) => number;
  holder?: string;
}

export const AWARDS: AwardDef[] = [
  { icon: '🐟', name: 'Master Angler', desc: 'most fish caught', pts: 15, get: (c) => c.caught },
  { icon: '🌈', name: 'Naturalist', desc: 'most species caught', pts: 15, get: (c) => c.species },
  { icon: '🗺️', name: 'Wayfarer', desc: 'most waters fished', pts: 15, get: (c) => c.areas },
  { icon: '🔨', name: 'Builder', desc: 'most infrastructure', pts: 15, get: (c) => c.infra },
  { icon: '⚙️', name: 'Outfitter', desc: 'most upgrades', pts: 10, get: (c) => c.upgrades },
  { icon: '🪵', name: 'Lumber Baron', desc: 'most wood on hand', pts: 10, get: (c) => c.wood },
];

export function goodsValue(): number {
  let v = 0;
  carryFish.concat(boatFish, cartFish).forEach((fish) => {
    v += Math.round(BASE_PRICE[fish.n] * freshMult(fish));
  });
  return v + pearls * PEARL_PRICE + Math.floor(wood * 1.2);
}

export interface PlayerInfraFlags {
  hasBoat: boolean;
  hasCart: boolean;
  hasShelter: boolean;
}

export interface RemoteScore extends PlayerStats {
  name: string;
  base: number;
}

// The trophy-relevant stats for the local player — shared by contestants()
// (the "You" row) and main.ts's score_update sends, so a remote peer's
// device can judge AWARDS fairly instead of seeing all-zero stats for you.
export function localTrophyStats(infra: PlayerInfraFlags): PlayerStats {
  return {
    caught: statCaught,
    species: speciesCounts.size,
    areas: fishedAreas.size,
    infra: jettyCount + (infra.hasBoat ? 1 : 0) + (infra.hasCart ? 1 : 0) + (infra.hasShelter ? 1 : 0),
    upgrades: (rodLevel - 1) + (boatLevel - 1) + (cartLevel - 1) + (hasNet ? 1 : 0),
    wood,
  };
}

export function contestants(infra: PlayerInfraFlags, remote: RemoteScore[] = []): Contestant[] {
  const list: Contestant[] = [{
    name: 'You', you: true,
    base: coins + goodsValue(),
    ...localTrophyStats(infra),
    pts: 0,
    trophies: '',
  }];
  rivals.forEach((rv) => list.push({
    name: rv.name,
    base: rv.coins + rivalCargoValue(rv),
    caught: rv.totalCaught,
    species: rv.species.size,
    areas: rv.areas,
    infra: (rv.gear >= 2 ? 1 : 0) + (rv.gear >= 3 ? 1 : 0),
    upgrades: rv.gear,
    wood: rv.wood,
    pts: 0,
    trophies: '',
  }));
  remote.forEach((r) => list.push({ ...r, pts: 0, trophies: '' }));
  list.forEach((c) => { c.pts = c.base; c.trophies = ''; });
  AWARDS.forEach((a) => {
    let best = 0;
    list.forEach((c) => { best = Math.max(best, a.get(c)); });
    if (best <= 0) return;
    a.holder = list.filter((c) => a.get(c) === best).map((c) => c.name).join(', ');
    list.forEach((c) => {
      if (a.get(c) === best) { c.pts += a.pts; c.trophies += a.icon; }
    });
  });
  list.sort((x, y) => y.pts - x.pts);
  return list;
}
